import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import dayjs from 'utils/dayjs'

import { DateRangeValue } from '../../DateRange.types'
import {
  formatDateForInput,
  formatTimeForInput,
  getDateFromInputValue,
  replaceDateTimeWithTime,
} from '../DateRangeInputs.utils'

// NOTE: this is used to disable the input label/placeholder animation
// when the input value isn't truthy (i.e. empty string)
export const nbsp = '\u00A0'

export const useDateRangeInputs = ({
  range,
  hoveredDay,
  format,
  onRangeChange,
}: {
  range: DateRangeValue | undefined
  hoveredDay?: Date
  format: 'date' | 'datetime'
  onRangeChange: (range: DateRangeValue | undefined) => void
}) => {
  const formatValueForInput = useMemo(
    () => (format === 'datetime' ? formatTimeForInput : formatDateForInput),
    [format]
  )
  const formattedData = useMemo(
    () => ({
      start: range?.start ? formatValueForInput(range.start) : nbsp,
      end: range?.end ? formatValueForInput(range.end) : nbsp,
    }),
    [range?.start, range?.end, formatValueForInput]
  )

  const [startInputValue, setStartInputValue] = useState(formattedData.start)
  const [endInputValue, setEndInputValue] = useState(formattedData.end)

  useEffect(() => {
    setStartInputValue(formattedData.start)
    setEndInputValue(formattedData.end)
  }, [formattedData.start, formattedData.end, formattedData])

  const setInputValue = useCallback((value: string, key: 'start' | 'end') => {
    if (key === 'start') {
      setStartInputValue(value)
    } else {
      setEndInputValue(value)
    }
  }, [])

  const handleBlur = useCallback(
    (dateOrTimeValue: string, key: 'start' | 'end') => {
      const dateValue =
        range && format === 'datetime'
          ? replaceDateTimeWithTime(range[key], dateOrTimeValue)
          : getDateFromInputValue(dateOrTimeValue)

      const isDateAfterToday = dayjs(dateValue).isAfter(dayjs())
      const isDateValid =
        !!dateValue && dayjs(dateValue).isValid() && !isDateAfterToday

      if (!isDateValid) {
        setInputValue(formattedData[key], key)
        return
      }

      setInputValue(formatValueForInput(dateValue), key)

      const shouldSwapKeys = !!(
        (range?.end && key === 'start' && dateValue > range.end) ||
        (range?.start && key === 'end' && dateValue < range.start)
      )

      if (shouldSwapKeys) {
        onRangeChange({
          start: key === 'start' ? range.end : dateValue,
          end: key === 'end' ? range.start : dateValue,
        })
        return
      }

      onRangeChange({
        ...range,
        [key]: dateValue,
      })
    },
    [
      format,
      formatValueForInput,
      formattedData,
      onRangeChange,
      range,
      setInputValue,
    ]
  )

  const handleStartInputBlur = useCallback(() => {
    handleBlur(startInputValue, 'start')
  }, [startInputValue, handleBlur])

  const handleEndInputBlur = useCallback(() => {
    handleBlur(endInputValue, 'end')
  }, [handleBlur, endInputValue])

  const handleStartInputChange = useCallback(
    ({ target: { value } }) => {
      setStartInputValue(value)
    },
    [setStartInputValue]
  )

  const handleEndInputChange = useCallback(
    ({ target: { value } }) => {
      setEndInputValue(value)
    },
    [setEndInputValue]
  )

  const handleEndInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur()
      }
    },
    []
  )

  const endInputRef = useRef<HTMLInputElement>(null)
  const handleStartInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        endInputRef.current?.focus()
      }
    },
    []
  )

  useEffect(() => {
    if (!hoveredDay || !range?.start) return

    if (range.start && range.end) return

    if (hoveredDay < range.start) {
      setEndInputValue(formatValueForInput(range.start))
      setStartInputValue(nbsp)
    } else {
      setEndInputValue(nbsp)
      setStartInputValue(formatValueForInput(range.start))
    }
  }, [formatValueForInput, hoveredDay, range?.end, range?.start])

  return {
    formattedData,
    startInputValue,
    handleStartInputBlur,
    handleStartInputChange,
    handleStartInputKeyDown,
    handleEndInputBlur,
    handleEndInputChange,
    handleEndInputKeyDown,
    endInputRef,
    endInputValue,
  }
}
